import { z } from "zod";

/**
 * Mirrors `WorkflowTimelineEventWorkItem` on the wire. A small,
 * intentionally narrow projection of the work-item document — only the
 * fields the Timeline UI needs to render chat content inline. The full
 * `WorkItem` schema (with queue lists, awaiting-action sets, etc.) is
 * deliberately not used here so the audit feed stays decoupled from
 * the workflow detail shape.
 */
export const TimelineEventWorkItemSchema = z.object({
    id: z.string(),
    /** "message" | "task" | "note" — kept open since work-item types may grow. */
    type: z.string(),
    // Coerced to "" at the boundary: tasks may have no body, so the wire
    // value can be null/absent. Keeps downstream renderers on a string.
    body: z.string().nullish().transform((value) => value ?? ""),
    createdByDisplayName: z.string().nullable().optional().default(null),
    completedAt: z.string().nullable().optional().default(null),
});

export type TimelineEventWorkItem = z.infer<typeof TimelineEventWorkItemSchema>;

/**
 * Mirrors `WorkflowTimelineEvent` on the wire. The shape matches the
 * Activity Ledger record (`activityId`, `actorId`, ...) plus the joined
 * `workItem` snapshot when the event keys against a work item that
 * still belongs to this workflow.
 *
 * `data` is left as `unknown` because the ledger payload is open: each
 * activity type owns its own JSON shape (the Einstein backend captures
 * an anonymous object via `ActivityData.Capture`, surfaced as `{ raw: ... }`).
 * The per-type renderers narrow this with their own zod parses on demand.
 */
export const TimelineEventSchema = z.object({
    activityId: z.string(),
    parentActivityId: z.string().nullable().optional().default(null),
    correlationId: z.string(),
    occurredAt: z.string(),
    activityType: z.string(),
    entityType: z.string(),
    entityId: z.string(),
    actorId: z.string(),
    /** "human" | "system". Open enum — preserve unknown values for forward compat. */
    actorType: z.string(),
    actorDisplayName: z.string().nullable().optional().default(null),
    data: z.unknown().nullable().optional(),
    workItem: TimelineEventWorkItemSchema.nullable().optional().default(null),
});

export type TimelineEvent = z.infer<typeof TimelineEventSchema>;

/**
 * Mirrors `WorkflowTimelineResponse`. `truncated` and `partial` are
 * disclosure flags the UI surfaces as inline notes rather than blocking
 * banners — the timeline is an audit view, missing-data is part of the
 * contract, not an error case.
 */
export const WorkflowTimelineResponseSchema = z.object({
    events: z.array(TimelineEventSchema),
    truncated: z.boolean(),
    partial: z.boolean(),
});

export type WorkflowTimelineResponse = z.infer<typeof WorkflowTimelineResponseSchema>;
